import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Package, Monitor, Database, Users, Shield,
  Cpu, Server, Layers, ArrowRight, Sparkles
} from 'lucide-react'

const softwareProducts = [
  {
    icon: Monitor,
    name: 'Project Tracking App',
    description: 'Real-time project monitoring with milestone tracking, progress reports, and resource allocation for government projects.',
    features: ['Dashboard Analytics', 'Milestone Tracking', 'Progress Reports'],
    color: '#6366f1',
  },
  {
    icon: Database,
    name: 'Budget Record System',
    description: 'Complete financial management with budget allocation, expense tracking, and audit compliance for institutions.',
    features: ['Budget Planning', 'Expense Tracking', 'Audit Trail'],
    color: '#8b5cf6',
  },
  {
    icon: Shield,
    name: 'Smart Card Application',
    description: 'Secure identification system with biometric authentication, access management, and attendance tracking.',
    features: ['Biometric Auth', 'Access Control', 'Attendance'],
    color: '#ec4899',
  },
  {
    icon: Users,
    name: 'HR & Inventory System',
    description: 'Integrated HR management with payroll, recruitment, inventory tracking, and leave management.',
    features: ['Payroll', 'Inventory', 'Leave Management'],
    color: '#06b6d4',
  },
]

const infrastructureProducts = [
  {
    icon: Monitor,
    name: 'IT Labs Setup',
    description: 'Modern computer laboratories with high-performance PCs, networking, and educational software installation.',
    features: ['High-Performance PCs', 'Network Setup', 'Software'],
    color: '#6366f1',
  },
  {
    icon: Cpu,
    name: 'Math & Robotics Labs',
    description: 'STEM-focused learning infrastructure with robotics kits, programming tools, and experiment stations.',
    features: ['Robotics Kits', 'Programming Tools', 'STEM'],
    color: '#8b5cf6',
  },
  {
    icon: Layers,
    name: 'Smart Classrooms',
    description: 'Interactive displays, digital whiteboards, audio systems, and multimedia learning environments.',
    features: ['Interactive Displays', 'Whiteboards', 'Audio'],
    color: '#ec4899',
  },
  {
    icon: Package,
    name: 'Office Furniture & Compactors',
    description: 'Premium office furniture including desks, chairs, workstations, and high-density mobile storage systems.',
    features: ['Ergonomic Design', 'Compactors', 'Storage'],
    color: '#f97316',
  },
]

export const ProductsGrid = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <section ref={ref} className="py-24 relative">
      <div className="container-enterprise relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
              boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
            }}
          >
            <Package className="w-4 h-4 text-primary" />
            <span className="text-sm text-slate-300">Product Portfolio</span>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Our <span className="gradient-text">Products</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-3xl mx-auto">
            Proprietary software solutions and infrastructure products designed for government, education, healthcare, and enterprise sectors.
          </p>
        </motion.div>

        {/* Software Products */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
              }}
            >
              <Monitor className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Software Solutions</h3>
              <p className="text-sm text-slate-400">Enterprise software for government & institutions</p>
            </div>
            <Link to="/products" className="ml-auto text-sm text-primary hover:text-primary/80 transition-colors flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {softwareProducts.map((product, index) => {
              const Icon = product.icon
              return (
                <motion.div
                  key={product.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
                  className="group"
                >
                  <Link
                    to="/products"
                    className="block h-full rounded-2xl p-6 transition-all duration-300 hover:translate-y-[-4px]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `${product.color}20`,
                        border: `1px solid ${product.color}40`,
                      }}
                    >
                      <Icon className="w-7 h-7" style={{ color: product.color }} />
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                      {product.name}
                    </h4>
                    <p className="text-sm text-slate-400 mb-4 leading-relaxed">{product.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {product.features.map((feature, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 rounded-full"
                          style={{
                            background: `${product.color}15`,
                            color: product.color,
                            border: `1px solid ${product.color}20`,
                          }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Infrastructure Products */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
              }}
            >
              <Server className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Infrastructure Products</h3>
              <p className="text-sm text-slate-400">Physical infrastructure & equipment</p>
            </div>
            <Link to="/products" className="ml-auto text-sm text-emerald-400 hover:text-emerald-400/80 transition-colors flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {infrastructureProducts.map((product, index) => {
              const Icon = product.icon
              return (
                <motion.div
                  key={product.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.05 }}
                  className="group"
                >
                  <Link
                    to="/products"
                    className="block h-full rounded-2xl p-6 transition-all duration-300 hover:translate-y-[-4px]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `${product.color}20`,
                        border: `1px solid ${product.color}40`,
                      }}
                    >
                      <Icon className="w-7 h-7" style={{ color: product.color }} />
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors">
                      {product.name}
                    </h4>
                    <p className="text-sm text-slate-400 mb-4 leading-relaxed">{product.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {product.features.map((feature, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 rounded-full"
                          style={{
                            background: `${product.color}15`,
                            color: product.color,
                            border: `1px solid ${product.color}20`,
                          }}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </Link>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Stats & CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16"
        >
          <div className="grid md:grid-cols-4 gap-4 mb-10">
            {[
              { value: '8+', label: 'Software Products' },
              { value: '10+', label: 'Infrastructure Products' },
              { value: '50+', label: 'Institutions Served' },
              { value: '100%', label: 'Quality Assured' },
            ].map((stat) => (
              <div key={stat.label}
                className="text-center p-5 rounded-2xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/products"
              className="btn-glow inline-flex items-center gap-2 px-8 py-4 text-white font-semibold rounded-xl"
            >
              <Sparkles className="w-5 h-5" />
              <span>Explore All Products</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  )
}